import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  ButtonBase,
  Typography,
  Avatar,
  Tooltip,
  useMediaQuery,
  Breadcrumbs,
  Link as MuiLink,
  Collapse,
  Menu,
  MenuItem,
  Select,
  Badge,
  Chip,
  InputBase,
  Alert,
  Paper,
  Stack,
  Fade,
  TextField,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { AppDialog } from '../components/dialog';
import { usePermissions, Capability } from "../ui/PermissionsProvider";
import { useBootstrap } from '../ui/BootstrapProvider';
import { findTrail, forPerson } from '../navigation/match';
import { navIcon } from '../navigation/icons';
import { SidebarFlyout } from '../navigation/SidebarFlyout';
import { homePath } from '../navigation/home';
import type { NavGroup, NavLink } from '../navigation/types';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import LogoutIcon from '@mui/icons-material/Logout';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import ViewSidebarRounded from '@mui/icons-material/ViewSidebarRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeRounded from '@mui/icons-material/HomeRounded';
// Minimal icons are used for the menu; MUI icons remain for controls
import AddRounded from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import { ModeContext } from "../theme/ThemeProvider";
import { SnackbarContext } from "../ui/SnackbarProvider";
import SyncBanner from "../ui/SyncBanner";
import PageBanner from "../ui/PageBanner";
import GuestBanner from "../ui/GuestBanner";
import SupportRequestDialog from "../components/SupportRequestDialog";
import { useProductOnboarding } from "../hooks/useProductOnboarding";
import { getToken, isTokenExpired, clearAuthStorage } from "../utils/auth";
import { buildLoginUrl } from "../utils/loginRedirect";
import { config, buildApiUrl } from "../config";
// Sidebar menu icons: Material-UI Rounded style for consistency
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import DashboardRounded from '@mui/icons-material/DashboardRounded';
import CableRounded from '@mui/icons-material/CableRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import IosShare from '@mui/icons-material/IosShare';
import EditRounded from '@mui/icons-material/EditRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import QuestionAnswerRounded from '@mui/icons-material/QuestionAnswerRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import TimelineRounded from '@mui/icons-material/TimelineRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import FlightTakeoffRounded from '@mui/icons-material/FlightTakeoffRounded';
import CelebrationRounded from '@mui/icons-material/CelebrationRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { apiCache } from "../utils/apiCache";
import {
  getOnboardingDataset,
  getOnboardingFirstQ,
  getOnboardingPath,
  getOnboardingTtvStartMs,
} from "../utils/onboardingLocalStorage";
import {
  trackOnboardingCompletionCtaClicked,
  trackOnboardingCompletionModalDismissed,
  trackOnboardingCompletionModalViewed,
} from "../utils/analytics";
import { CopilotSidebar } from './copilot/components/CopilotSidebar';
import AgentSidebar, { AGENT_NAMES, AGENT_SECTIONS } from './agents/AgentSidebar';
import { isPlaybookId } from './agents/playbookId';
import { kindOfAddress } from './agents/agentAddress';
import { ONBOARDING_SESSION_STORAGE_KEY } from "./onboarding/hooks/useOnboardingSession";

// Sidebar / header dimensions are sourced from the design system so they
// stay consistent with the rest of the chrome. Tweak in theme/designSystem.ts
// (`sizes.sidebar`, `sizes.header`) to update the entire app at once.
import { sizes as DS_SIZES } from '../theme/designSystem';
const drawerWidth = DS_SIZES.sidebar.expanded;
const miniWidth = DS_SIZES.sidebar.collapsed;
const headerHeight = DS_SIZES.header;

/** `100dvh` tracks the visible viewport on mobile Safari; plain `100vh` jumps when browser chrome shows/hides. */
const viewportHeightSx = {
  height: '100vh',
  '@supports (height: 100dvh)': {
    height: '100dvh',
  },
} as const;

type SidebarMenuItem = {
  key: string;
  label: string;
  path: string;
  icon: typeof ChatBubbleOutlineRounded;
  /** The workspace must have this feature switched on. */
  requiresFeature?: string;
  /** This person must hold this capability. Both gates must pass. */
  requiresCapability?: Capability;
  badge?: string;
};

// Label lookups for breadcrumbs and page titles. The sidebar itself is drawn
// from what the server sends (backend/services/navigation/default.yaml plus
// the tenant's own edits), not from these lists.
const mainMenu: SidebarMenuItem[] = [
  { key: "copilot", label: "Copilot", path: "/copilot", icon: ChatBubbleOutlineRounded },
  { key: "playbooks", label: "Agents", path: "/agents", icon: SmartToyOutlined },
  { key: "dashboard", label: "Dashboard", path: "/dashboard", icon: DashboardRounded },
  { key: "spaces", label: "Shared Space", path: "/spaces", icon: MenuBookRounded },
  { key: "connectors", label: "Connectors", path: "/connectors", icon: CableRounded },
  { key: "data-model", label: "Data Model", path: "/data-model", icon: AccountTreeRounded },
  { key: "knowledge", label: "Knowledge Base", path: "/knowledge", icon: AutoAwesomeRounded },
];

const liveMonitoringMenuItems: SidebarMenuItem[] = [
  {
    key: "lead-to-cash",
    label: "Lead-to-Cash",
    path: "/lead-to-cash",
    icon: TimelineRounded,
    requiresFeature: "lead_to_cash_monitor",
    badge: "Beta",
  },
  {
    key: "dialing-opportunity",
    label: "Dialing",
    path: "/monitoring/dialing-opportunity",
    icon: PhoneInTalkRounded,
    requiresFeature: "dialing_opportunity_monitor",
    badge: "Beta",
  },
  {
    key: "contact-optimization",
    label: "Contact Pulse",
    path: "/monitoring/contact-optimization",
    icon: PhoneInTalkRounded,
    requiresFeature: "contact_optimization",
    badge: "New",
  },
  {
    key: "broker-monitor",
    label: "Broker Activity",
    path: "/broker-monitor",
    icon: FlightTakeoffRounded,
    requiresFeature: "broker_monitor",
    badge: "Beta",
  },
];

// Settings nav — split by audience (admin vs. workspace vs. you) so each
// sub-page has a single, focused job. Order roughly mirrors how often
// users will visit them: AI > Workspace > Branding > Account.
const settingsMenuItems = [
  { key: "settings-ai", label: "AI Configuration", path: "/settings/ai", icon: TuneRounded, requiresCapability: "manage_workspace" },
  { key: "settings-workspace", label: "Workspace", path: "/settings/workspace", icon: ViewSidebarRounded, requiresCapability: "manage_workspace" },
  { key: "settings-features", label: "Features", path: "/settings/features", icon: FlagRounded, requiresCapability: "manage_features" },
  { key: "settings-branding", label: "Branding", path: "/settings/branding", icon: PaletteRounded, requiresCapability: "manage_workspace" },
  // Account is deliberately absent. Password, persona and profile are
  // personal, and Settings is now an admin-only section — a member has to be
  // able to reach their own account. It lives in the avatar menu instead.
];


export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  // The sidebar the server built for this workspace, narrowed to what this
  // person may open, and the row the current address belongs to.
  const { navigation, loading: bootstrapLoading } = useBootstrap();
  const sidebar = React.useMemo(() => forPerson(navigation, can), [navigation, can]);
  const trail = React.useMemo(() => findTrail(sidebar, location.pathname || '/'), [sidebar, location.pathname]);
  // The sidebar row the address belongs to. Pages no row claims (account
  // settings, a board opened from the list) fall back to the keys below,
  // which breadcrumbs and page titles still read.
  const activeKey = React.useMemo(() => {
    if (trail) return trail.link.id;
    const p = location.pathname || '';
    // `/` is Copilot home (see App.tsx index route).
    if (p === '/' || p === '/copilot' || p.startsWith('/copilot/')) return 'copilot';
    // The dashboards section's categories sit under /dashboards/…, which would
    // otherwise match the dashboard list's own prefix below.
    if (p.startsWith('/dashboards/')) return `dashboards-${p.split('/')[2]}`;
    if (p.startsWith('/dashboard')) return 'dashboard';
    if (p.startsWith('/lead-to-cash')) return 'lead-to-cash';
    if (p.startsWith('/monitoring/dialing-opportunity')) return 'dialing-opportunity';
    if (p.startsWith('/sales-operations/plan')) return 'sales-ops-plan';
    if (p.startsWith('/sales-operations/queue')) return 'sales-ops-queue';
    if (p.startsWith('/sales-operations/agents')) return 'sales-ops-agents';
    if (p.startsWith('/sales-operations')) return 'sales-ops-today';
    if (p.startsWith('/monitoring/contact-optimization')) return 'contact-optimization';
    if (p.startsWith('/broker-monitor')) return 'broker-monitor';
    if (p.startsWith('/spaces')) return 'spaces';
    if (p.startsWith('/saved-queries')) return 'spaces'; // Backwards compatibility
    if (p.startsWith('/sales-desk')) return 'acme-sales-desk';
    if (p.startsWith('/agents')) return 'playbooks';
    if (p.startsWith('/connectors')) return 'connectors';
    if (p.startsWith('/data-model')) return 'data-model';
    // Build Data Model and its runs belong to Data Model. Unclaimed, they fell
    // through to the default below and lit up Copilot.
    if (p === '/data-sync' || p.startsWith('/data-sync/')) return 'data-model';
    if (p.startsWith('/bcl')) return 'bcl';
    if (p.startsWith('/knowledge')) return 'knowledge';
    if (p.startsWith('/predictive-models')) return 'predictive-models';
    if (p.startsWith('/settings/ai') || p === '/settings') return 'settings-ai';
    if (p.startsWith('/settings/workspace')) return 'settings-workspace';
    if (p.startsWith('/settings/features')) return 'settings-features';
    if (p.startsWith('/settings/branding') || p.startsWith('/branding')) return 'settings-branding';
    // /settings/organization redirects to /settings/account — see App.tsx.
    if (p.startsWith('/settings/account') || p.startsWith('/settings/organization') || p.startsWith('/profile')) return 'settings-account';
    if (p.startsWith('/users')) return 'users';
    return 'copilot';
  }, [location.pathname, trail]);
  const isMobile = useMediaQuery('(max-width:900px)');
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const brandingContext = React.useContext(ModeContext);
  const sidebarPosition = brandingContext.branding?.sidebarPosition || 'left';
  const { notify } = React.useContext(SnackbarContext);
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    const stored = localStorage.getItem('sidebar_collapsed');
    // If no value in localStorage (new user), default to open (false)
    if (stored === null) return false;
    return stored !== '0';
  });
  // A board opened at its old address (/dashboard/<id>/…) — a bookmark, a
  // link from another page — moves to the readable one its sidebar row gives
  // it, so the address bar always says where the reader is.
  React.useEffect(() => {
    const link = trail?.link;
    if (link?.internal_path && link.internal_path === location.pathname && link.path !== location.pathname) {
      navigate(link.path + location.search + location.hash, { replace: true, state: location.state });
    }
  }, [trail, location.pathname, location.search, location.hash, location.state, navigate]);

  // Which groups are open. A group opens by itself when you land on one of
  // its rows, so a deep link never hides where you are.
  const [openGroups, setOpenGroups] = React.useState<Set<string>>(() => new Set());
  const activeGroupId = trail?.group?.id ?? null;
  React.useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) => (prev.has(activeGroupId) ? prev : new Set(prev).add(activeGroupId)));
  }, [activeGroupId]);
  const toggleGroup = React.useCallback((id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  // Sidebar no longer auto-expands on hover. Use explicit toggle.
  const effectiveCollapsed = !isMobile ? collapsed : false;
  // Copilot session sidebar only for /copilot/:threadId - not /, /copilot, or /chat
  const isCopilotActive = activeKey === 'copilot' && location.pathname.startsWith('/copilot/');
  // Inside an agent the sidebar becomes that agent's own: what feeds it, how
  // it decides, what it did. An agent is a small application and a handful of
  // tabs could not carry it — the way back out is the first row in the column.
  // A workspace agent is addressed by its kind (/agents/rocco/…); a saved
  // analysis by its id (/agents/<uuid>), which keeps the ordinary sidebar.
  const isAgentActive = /^\/agents\/[^/]+/.test(location.pathname)
    && !isPlaybookId(location.pathname.split('/')[2]);
  const [l2cChatSplit, setL2cChatSplit] = React.useState(false);
  React.useEffect(() => {
    const onSplit = (e: Event) => {
      const detail = (e as CustomEvent<{ active?: boolean }>).detail;
      setL2cChatSplit(Boolean(detail?.active));
    };
    window.addEventListener('klaris:l2c-chat-split', onSplit);
    return () => {
      window.removeEventListener('klaris:l2c-chat-split', onSplit);
      setL2cChatSplit(false);
    };
  }, []);
  // Data-dense boards supply their own inset and want the full column width;
  // the page-level gutter costs them a filter column and a table column.
  // Signalled by the page rather than read off the URL, since spec-driven
  // boards share /dashboard/<id> with the widget grid.
  const [isWideBoard, setIsWideBoard] = React.useState(false);
  const [wideBoardFullHeight, setWideBoardFullHeight] = React.useState(false);
  React.useEffect(() => {
    const onWide = (e: Event) => {
      const detail = (e as CustomEvent<{ active?: boolean; fullHeight?: boolean }>).detail;
      setIsWideBoard(Boolean(detail?.active));
      setWideBoardFullHeight(Boolean(detail?.active) && Boolean(detail?.fullHeight));
    };
    window.addEventListener('klaris:wide-board', onWide);
    return () => {
      window.removeEventListener('klaris:wide-board', onWide);
      setIsWideBoard(false);
      setWideBoardFullHeight(false);
    };
  }, []);
  const isFullHeightPage = isCopilotActive || l2cChatSplit || wideBoardFullHeight;
  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((prev: boolean) => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', next ? '1' : '0');
      return next;
    });
  }, []);
  const [settingsEl, setSettingsEl] = React.useState<null | HTMLElement>(null);
  const openSettings = Boolean(settingsEl);
  const handleOpenSettings = (e: React.MouseEvent<HTMLButtonElement>) => setSettingsEl(e.currentTarget);
  const handleCloseSettings = () => setSettingsEl(null);
  
  // User profile menu state
  const [userMenuEl, setUserMenuEl] = React.useState<null | HTMLElement>(null);
  const openUserMenu = Boolean(userMenuEl);
  const handleOpenUserMenu = (e: React.MouseEvent<HTMLElement>) => setUserMenuEl(e.currentTarget);
  const handleCloseUserMenu = () => setUserMenuEl(null);
  const [supportOpen, setSupportOpen] = React.useState(false);

  // Get user info from localStorage
  const userInfo = React.useMemo(() => {
    try {
      const userData = window.localStorage.getItem('klaris_user');
      const tenantData = window.localStorage.getItem('klaris_tenant');
      return {
        user: userData ? JSON.parse(userData) : null,
        tenant: tenantData ? JSON.parse(tenantData) : null,
      };
    } catch {
      return { user: null, tenant: null };
    }
  }, []);
  const tenantId = React.useMemo(() => {
    try { const t = JSON.parse(String(window.localStorage.getItem('klaris_tenant'))); return String(t?.tenant_id || ''); } catch { return ''; }
  }, []);

  // Both dashboard kinds live under the same breadcrumb and title; only the
  // renderer differs, so the layout treats their two routes alike.
  const currentDashboardId = React.useMemo(() => {
    const prefix = '/dashboard/';
    return location.pathname.startsWith(prefix)
      ? decodeURIComponent(location.pathname.slice(prefix.length))
      : null;
  }, [location.pathname]);
  const [dashboardTitle, setDashboardTitle] = React.useState<string>('Dashboard');
  const [dashboardDescription, setDashboardDescription] = React.useState<string>(`Welcome to ${brandingContext.branding?.name || config.brandName}`);
  const [dashboards, setDashboards] = React.useState<Array<{ dashboard_id: string; name: string }>>([]);
  
  // Space state management
  const currentSpaceId = React.useMemo(() => (
    location.pathname.startsWith('/spaces/') ? decodeURIComponent(location.pathname.slice('/spaces/'.length)) : null
  ), [location.pathname]);
  const [spaceName, setSpaceName] = React.useState<string>('Space');
  // Footer is part of the normal document flow now (non-sticky)

  // Dashboard rename state
  const [renamingDashboard, setRenamingDashboard] = React.useState(false);
  const [dashboardRenameValue, setDashboardRenameValue] = React.useState('');
  const [dashboardRenamingSaving, setDashboardRenamingSaving] = React.useState(false);

  const [onboardingDoneModalOpen, setOnboardingDoneModalOpen] = React.useState(false);
  const {
    steps: onboardingSteps,
    completedCount: onboardingCompletedCount,
    totalCount: onboardingTotalCount,
    loading: onboardingLoading,
    isSelfServeProductOnboarding,
  } = useProductOnboarding();
  const onboardingAllDone = !onboardingLoading
    && onboardingTotalCount > 0
    && onboardingCompletedCount === onboardingTotalCount;
  const onboardingDoneSeenKey = React.useMemo(() => {
    const userKey = userInfo?.user?.user_id || userInfo?.user?.email || 'user';
    const tenantKey = tenantId || 'tenant';
    return `klaris_onboarding_done_modal_seen:${tenantKey}:${userKey}`;
  }, [tenantId, userInfo?.user?.user_id, userInfo?.user?.email]);

  React.useEffect(() => {
    if (!onboardingAllDone || !isSelfServeProductOnboarding) return;
    try {
      const seen = localStorage.getItem(onboardingDoneSeenKey) === '1';
      if (!seen) setOnboardingDoneModalOpen(true);
    } catch {
      setOnboardingDoneModalOpen(true);
    }
  }, [onboardingAllDone, onboardingDoneSeenKey, isSelfServeProductOnboarding]);

  const closeOnboardingDoneModal = React.useCallback(() => {
    setOnboardingDoneModalOpen(false);
    try { localStorage.setItem(onboardingDoneSeenKey, '1'); } catch { /* ignore */ }
  }, [onboardingDoneSeenKey]);

  const onboardingCompletionViewLoggedRef = React.useRef(false);
  React.useEffect(() => {
    if (!onboardingDoneModalOpen) {
      onboardingCompletionViewLoggedRef.current = false;
      return;
    }
    if (!tenantId || onboardingCompletionViewLoggedRef.current) return;
    onboardingCompletionViewLoggedRef.current = true;
    const pathHint = getOnboardingPath(tenantId);
    const datasetName = getOnboardingDataset(tenantId);
    const isSampleData = pathHint === 'sample' || !!datasetName;
    const startTs = getOnboardingTtvStartMs(tenantId);
    const elapsedMs = startTs ? Date.now() - startTs : 0;
    const elapsedMin = Math.max(1, Math.round(elapsedMs / 60000));
    const MEANINGFUL_CLOCK_MS = 90 * 60 * 1000;
    const clockStillMeaningful = startTs > 0 && elapsedMs <= MEANINGFUL_CLOCK_MS;
    trackOnboardingCompletionModalViewed({
      is_sample_data: isSampleData,
      path_hint: pathHint,
      clock_meaningful: clockStillMeaningful,
      minutes_shown: isSampleData && clockStillMeaningful ? elapsedMin : null,
    });
  }, [onboardingDoneModalOpen, tenantId]);

  const handleOnboardingCompletionDialogClose = React.useCallback(
    (_e: React.SyntheticEvent, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick') trackOnboardingCompletionModalDismissed({ action: 'backdrop' });
      else if (reason === 'escapeKeyDown') trackOnboardingCompletionModalDismissed({ action: 'escape' });
      closeOnboardingDoneModal();
    },
    [closeOnboardingDoneModal],
  );

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  // Listen for dashboard metadata from navigation state (passed from listing page or detail page)
  React.useEffect(() => {
    // Check if location state has dashboard info
    const state = location.state as { dashboardName?: string; dashboardDescription?: string } | null;
    if (state?.dashboardName) {
      setDashboardTitle(state.dashboardName);
      setDashboardDescription(state.dashboardDescription || '');
      return; // No need to fetch if we have the data
    }
    
    // Fallback: fetch dashboard metadata if not passed via state
    (async () => {
      try {
        if (!tenantId || !currentDashboardId) return;
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(currentDashboardId))}`), {
          headers: { Authorization: `Bearer ${token}` }
        } as RequestInit);
        const data = await resp.json();
        if (resp.ok) {
          setDashboardTitle(String(data?.name || 'Dashboard'));
          setDashboardDescription(String(data?.description || ''));
        }
      } catch {
        // ignore
      }
    })();
  }, [tenantId, currentDashboardId, location.state]);

  // Fetch space metadata if viewing a space detail page
  React.useEffect(() => {
    (async () => {
      try {
        if (!currentSpaceId) return;
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/spaces/${encodeURIComponent(String(currentSpaceId))}/queries`), {
          headers: { Authorization: `Bearer ${token}` }
        } as RequestInit);
        const data = await resp.json();
        if (resp.ok && data.space) {
          setSpaceName(String(data.space.name || 'Space'));
        }
      } catch {
        // ignore
      }
    })();
  }, [currentSpaceId]);

  // Lazy load dashboards list - only fetch when needed (not on initial mount)
  const fetchDashboards = React.useCallback(async () => {
    if (dashboards.length > 0) return; // Already loaded
    
    try {
      if (!tenantId) return;
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards`), {
        headers: { Authorization: `Bearer ${token}` },
      } as RequestInit);
      const data = await resp.json();
      if (resp.ok) {
        const list = Array.isArray(data?.dashboards) ? data.dashboards : [];
        setDashboards(list.map((d: any) => ({ dashboard_id: String(d.dashboard_id), name: String(d.name || 'Untitled') })));
      }
    } catch {}
  }, [tenantId, dashboards.length]);

  // Auth guard: check token on route change and on mount.
  // We capture `pathname + search` before bouncing so that after sign-in the
  // user lands back on the deep link they were trying to visit (shared URLs).
  React.useEffect(() => {
    const bounceToLogin = () => {
      const target = location.pathname + location.search;
      clearAuthStorage();
      notify('Your session has expired. Please login again.', 'warning');
      navigate(buildLoginUrl('/login', target), { replace: true });
    };
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      bounceToLogin();
    }
    // Also set up periodic check (every 60s)
    const id = window.setInterval(() => {
      const t = getToken();
      if (!t || isTokenExpired(t)) {
        bounceToLogin();
      }
    }, 60000);
    return () => window.clearInterval(id);
  }, [location.pathname, location.search, navigate, notify]);

  /** After OTP, block main app until signup wizard `current_step` is `completed` (same API as OnboardingWizard). */
  const onboardingWizardCompletedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const token = getToken();
    if (!token || isTokenExpired(token)) return;

    let sessionId: string | null = null;
    try {
      sessionId = window.localStorage.getItem(ONBOARDING_SESSION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (!sessionId) {
      onboardingWizardCompletedRef.current = null;
      return;
    }
    if (onboardingWizardCompletedRef.current === sessionId) return;

    const ac = new AbortController();
    void (async () => {
      try {
        const resp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/onboarding/sessions/${encodeURIComponent(sessionId)}`),
          { headers: { Authorization: `Bearer ${token}` }, signal: ac.signal },
        );
        if (!resp.ok) {
          if (resp.status === 404) {
            try {
              window.localStorage.removeItem(ONBOARDING_SESSION_STORAGE_KEY);
            } catch {
              /* ignore */
            }
          }
          return;
        }
        const data = (await resp.json()) as { current_step?: string };
        const step = data?.current_step;
        if (step && step !== 'completed') {
          navigate('/onboarding', { replace: true });
          return;
        }
        onboardingWizardCompletedRef.current = sessionId;
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === 'AbortError') return;
      }
    })();
    return () => ac.abort();
  }, [location.pathname, navigate]);



  /**
   * Modern enterprise nav item style:
   *   - 8px corners (matches design-system `radius.sm`)
   *   - subtle hover, brand-tinted active fill
   *   - 2px brand-color rail on the left edge when active
   *   - icons sit at 18px and pick up the brand color when active
   *   - content is horizontally inset so the active fill never bleeds to the
   *     drawer edge — a key tell of polished sidebars (Linear, Notion, Hex)
   */
  const navItemSx = React.useCallback(
    (collapsed: boolean, active: boolean) => (theme: Theme) => ({
      position: 'relative',
      borderRadius: `${theme.ds.radius.sm}px`,
      minHeight: 34,
      px: collapsed ? 0 : 1.25,
      py: 0,
      mx: 0,
      mb: 0.25,
      width: collapsed ? 36 : 'auto',
      ...(collapsed ? { height: 36, alignSelf: 'center' } : {}),
      justifyContent: collapsed ? 'center' : 'flex-start',
      color: active ? theme.tokens.sidebar.itemActiveFg : theme.tokens.sidebar.itemFg,
      transition: `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}, color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
      '&:hover': {
        bgcolor: active ? theme.tokens.sidebar.itemActiveBg : theme.tokens.sidebar.itemHoverBg,
        color: active ? theme.tokens.sidebar.itemActiveFg : theme.tokens.text.primary,
      },
      '&.Mui-selected': {
        bgcolor: theme.tokens.sidebar.itemActiveBg,
        color: theme.tokens.sidebar.itemActiveFg,
        '&:hover': { bgcolor: theme.tokens.sidebar.itemActiveBg },
        // 2px brand rail on the leading edge — only when expanded; collapsed
        // mode lights up the icon itself so the rail is unnecessary.
        ...(collapsed ? {} : {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 6,
            bottom: 6,
            left: 0,
            width: 2,
            borderRadius: 2,
            bgcolor: theme.tokens.sidebar.itemActiveAccent,
          },
        }),
      },
      '& .menu-ic': {
        fontSize: 18,
        color: active ? theme.tokens.sidebar.iconActive : theme.tokens.sidebar.iconRest,
        transition: `color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
      },
      '&:hover .menu-ic': {
        color: active ? theme.tokens.sidebar.iconActive : theme.tokens.text.primary,
      },
      '& .MuiListItemText-root': { display: collapsed ? 'none' : 'block', m: 0 },
      '&.Mui-selected .MuiListItemText-primary': { color: theme.tokens.sidebar.itemActiveFg },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: theme.tokens.shadow.focusRing,
      },
    }),
    [],
  );

  /**
   * Sub-item chrome for the expandable nav groups (Settings, Sales
   * Operations): quieter than a top-level row, no left rail, indented with a
   * brand dot that fills in when active.
   *
   * Extracted so the two accordions cannot drift apart — this was inline in
   * the Settings block, and a second copy would have been thirty lines of
   * styling maintained in two places.
   */
  const submenuItemSx = React.useCallback(
    (isActive: boolean) => (theme: Theme) => ({
      position: 'relative' as const,
      borderRadius: `${theme.ds.radius.sm}px`,
      minHeight: 30,
      // Indented past the group's icon so the rows read as inside it, but no
      // further than the long names need: bold "Campaign Mix & Staffing" has
      // to fit on one line in the expanded rail.
      pl: 5.75,
      pr: 1.25,
      py: 0,
      mb: 0.125,
      color: isActive ? theme.tokens.sidebar.itemActiveFg : theme.tokens.sidebar.itemFg,
      transition: `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}, color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
      '&:hover': { bgcolor: theme.tokens.sidebar.itemHoverBg, color: theme.tokens.text.primary },
      '&.Mui-selected': {
        bgcolor: 'transparent',
        color: theme.tokens.sidebar.itemActiveFg,
        fontWeight: 600,
        '&:hover': { bgcolor: theme.tokens.sidebar.itemHoverBg },
        '& .submenu-dot': {
          bgcolor: theme.tokens.brand.primary,
          transform: 'scale(1)',
        },
      },
      '& .submenu-ic': { fontSize: 16, color: 'inherit' },
      '& .MuiListItemText-primary': {
        fontWeight: isActive ? 600 : 500,
        fontSize: '0.78125rem',
      },
      // One line, always: a name too long for the rail ends in an ellipsis
      // (the full name is on hover) rather than wrapping the row in two.
      '& .MuiListItemText-root': { minWidth: 0 },
    }),
    [],
  );

  const goTo = (path: string) => { navigate(path); setMobileOpen(false); };

  /** A section's eyebrow; collapsed, a hairline takes its place (the first
   *  section needs neither). */
  const renderSectionLabel = (label: string, first: boolean) => (
    <Box sx={(theme) => ({
      px: 1.5,
      mt: first ? 0.5 : 2,
      mb: 0.75,
      height: 16,
      display: first && effectiveCollapsed ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
      ...(!first && effectiveCollapsed && {
        mx: 1,
        mt: 1.5,
        mb: 1.5,
        height: 'auto',
        borderTop: `1px solid ${theme.tokens.border.subtle}`,
      }),
    })}>
      {!effectiveCollapsed && (
        <Typography sx={(theme) => ({ ...theme.typography.eyebrow, color: theme.tokens.sidebar.sectionLabel })}>
          {label}
        </Typography>
      )}
    </Box>
  );

  const renderBadge = (badge: string) => (
    <Box sx={(theme) => ({
      ml: 'auto',
      px: 0.75,
      py: 0.125,
      borderRadius: `${theme.ds.radius.xs}px`,
      fontSize: '0.625rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: theme.palette.primary.main,
      bgcolor: theme.palette.primary.main + '18',
      border: `1px solid ${theme.palette.primary.main}40`,
      lineHeight: 1.4,
      flexShrink: 0,
    })}>
      {badge}
    </Box>
  );

  /** The icon, with the label as a tooltip when the rail is collapsed. */
  const renderRowIcon = (iconKey: string | undefined, tooltip: string) => {
    const Icon = navIcon(iconKey);
    return effectiveCollapsed ? (
      <Tooltip title={tooltip} placement="right">
        <Box className="nav-icon-wrap" sx={{ display: 'flex', alignItems: 'center' }}>
          <Icon className="menu-ic" sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>
    ) : (
      <Box className="nav-icon-wrap" sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
        <Icon className="menu-ic" sx={{ fontSize: 18 }} />
      </Box>
    );
  };

  const renderLink = (link: NavLink) => {
    const active = activeKey === link.id;
    return (
      <ListItemButton
        key={link.id}
        selected={active}
        disableRipple
        disableTouchRipple
        onClick={() => goTo(link.path)}
        sx={navItemSx(effectiveCollapsed, active)}
      >
        {renderRowIcon(link.icon, link.badge ? `${link.badge} · ${link.label}` : link.label)}
        <ListItemText
          primary={link.label}
          title={effectiveCollapsed ? undefined : link.label}
          primaryTypographyProps={{ fontWeight: active ? 600 : 500, fontSize: '0.8125rem', letterSpacing: '0.005em', noWrap: true }}
          sx={{ minWidth: 0 }}
        />
        {!effectiveCollapsed && link.badge && renderBadge(link.badge)}
      </ListItemButton>
    );
  };

  /**
   * A row that opens to show its own links, the way Settings does. Opening it
   * goes nowhere — it only shows what is inside. On the collapsed rail there
   * is nothing to open into, so it goes to its first link instead.
   */
  const renderGroup = (group: NavGroup) => {
    // Hidden rows still light the group up when you are on one of them; they
    // are only left out of the list it opens onto.
    const active = group.items.some((link) => link.id === activeKey);
    const shown = group.items.filter((link) => !link.hidden);
    if (shown.length === 0) return null;

    // Collapsed, the group is only its icon: its pages open in a small menu
    // beside it (SidebarFlyout) instead of the icon jumping to the first one.
    if (effectiveCollapsed) {
      const Icon = navIcon(group.icon);
      return (
        <SidebarFlyout key={group.id} label={group.label} links={shown} activeKey={activeKey} onPick={goTo}>
          {(trigger) => (
            <ListItemButton
              {...trigger}
              selected={active}
              disableRipple
              disableTouchRipple
              sx={navItemSx(true, active)}
            >
              <Box className="nav-icon-wrap" sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon className="menu-ic" sx={{ fontSize: 18 }} />
              </Box>
            </ListItemButton>
          )}
        </SidebarFlyout>
      );
    }

    const open = openGroups.has(group.id);
    return (
      <React.Fragment key={group.id}>
        <ListItemButton
          selected={active}
          disableRipple
          disableTouchRipple
          aria-expanded={open}
          onClick={() => toggleGroup(group.id)}
          sx={navItemSx(effectiveCollapsed, active)}
        >
          {renderRowIcon(group.icon, group.label)}
          {!effectiveCollapsed && (
            <>
              <ListItemText
                primary={group.label}
                title={group.label}
                primaryTypographyProps={{ fontWeight: active ? 600 : 500, fontSize: '0.8125rem', letterSpacing: '0.005em', noWrap: true }}
                sx={{ minWidth: 0 }}
              />
              {group.badge && renderBadge(group.badge)}
              {open
                ? <ExpandLess sx={(theme) => ({ fontSize: 16, color: theme.tokens.text.tertiary })} />
                : <ExpandMore sx={(theme) => ({ fontSize: 16, color: theme.tokens.text.tertiary })} />}
            </>
          )}
        </ListItemButton>
        {!effectiveCollapsed && open && shown.map((link) => {
          const linkActive = activeKey === link.id;
          return (
            <ListItemButton
              key={link.id}
              selected={linkActive}
              disableRipple
              disableTouchRipple
              onClick={() => goTo(link.path)}
              sx={submenuItemSx(linkActive)}
            >
              <Box className="submenu-dot" sx={(theme) => ({
                width: 5,
                height: 5,
                borderRadius: '50%',
                mr: 1.25,
                ml: -1.5,
                bgcolor: theme.tokens.border.strong,
                transform: 'scale(0.6)',
                transition: `transform ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}, background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
              })} />
              <ListItemText
                primary={link.label}
                title={link.label}
                primaryTypographyProps={{ fontWeight: linkActive ? 600 : 500, fontSize: '0.78125rem', noWrap: true }}
              />
              {link.badge && renderBadge(link.badge)}
            </ListItemButton>
          );
        })}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box
      onClick={(e) => {
        if (isMobile || !effectiveCollapsed) return;
        const target = e.target as HTMLElement;
        if (target.closest('button,[role="button"],a,.MuiListItemButton,.MuiIconButton-root')) return;
        toggleCollapsed();
      }}
      sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Sidebar Header with Logo and Brand — modern enterprise lockup:
          flat, single hairline divider, no fake shadow, type-led brand mark. */}
      <Box sx={(theme) => ({
        height: `${headerHeight}px`,
        px: effectiveCollapsed ? 1.5 : 2,
        borderBottom: `1px solid ${theme.tokens.border.subtle}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
      })}>
        <ButtonBase onClick={() => {
          navigate(homePath(navigation, brandingContext.branding?.defaultHomePage));
        }} sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          textAlign: 'left',
          borderRadius: `${theme.ds.radius.sm}px`,
          px: effectiveCollapsed ? 0.5 : 1,
          py: 0.75,
          width: effectiveCollapsed ? 'auto' : '100%',
          transition: `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
          '&:hover': { bgcolor: theme.tokens.sidebar.itemHoverBg },
        })} aria-label="Go to home">
          {/* Show logo/initials only if logo exists OR sidebar is collapsed */}
          {(brandingContext.branding?.logoUrl || effectiveCollapsed) && (
            brandingContext.branding?.logoUrl ? (
              <Box
                sx={{
                  position: 'relative',
                  width: (!effectiveCollapsed && brandingContext.branding?.logoType === 'wordmark') ? 'auto' : 32,
                  height: 32,
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={brandingContext.branding.logoUrl}
                  alt="Logo"
                  sx={{
                    width: (!effectiveCollapsed && brandingContext.branding?.logoType === 'wordmark') ? 'auto' : 32,
                    height: (!effectiveCollapsed && brandingContext.branding?.logoType === 'wordmark') ? 26 : 32,
                    maxWidth: (!effectiveCollapsed && brandingContext.branding?.logoType === 'wordmark') ? 160 : 32,
                    objectFit: 'contain',
                    objectPosition: 'left center',
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{ position: 'relative', width: 32, height: 32 }}
              >
                <Avatar
                  variant="rounded"
                  sx={(theme) => ({
                    width: 32,
                    height: 32,
                    borderRadius: `${theme.ds.radius.sm}px`,
                    bgcolor: brandingContext.branding?.colors?.primary?.['400'] || 'primary.main',
                    color: theme.tokens.brand.onPrimary,
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    boxShadow: 'none',
                  })}
                >
                  {(brandingContext.branding?.name || config.brandName)
                    .split(' ')
                    .map(word => word[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </Avatar>
              </Box>
            )
          )}
          {!effectiveCollapsed && brandingContext.branding?.logoType !== 'wordmark' && (
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography
                noWrap
                sx={(theme) => ({
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  letterSpacing: '-0.014em',
                  lineHeight: 1.2,
                  color: theme.tokens.text.primary,
                })}
              >
                {brandingContext.branding?.name || config.brandName}
              </Typography>
              {userInfo.tenant?.name && userInfo.tenant.name !== (brandingContext.branding?.name || config.brandName) && (
                <Typography
                  noWrap
                  sx={(theme) => ({
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: theme.tokens.text.tertiary,
                    lineHeight: 1.3,
                    mt: 0.125,
                  })}
                >
                  {userInfo.tenant.name}
                </Typography>
              )}
            </Box>
          )}
        </ButtonBase>
        
      </Box>
      {/* Copilot thread sidebar (results list) only on desktop; mobile uses the normal nav drawer */}
      {isAgentActive && !isMobile ? (
        <AgentSidebar collapsed={effectiveCollapsed} />
      ) : isCopilotActive && !isMobile ? (
        <CopilotSidebar collapsed={effectiveCollapsed} />
      ) : (
      <List sx={{ flex: 1, px: 1.5, py: 2, overflowY: 'auto', minHeight: 0, pb: 3, '& .MuiListItemButton-root': { mb: 0.25 } }}>
        {/* Drawn from what the server sends: the default sidebar with this
            tenant's edits applied (backend/services/navigation). Sections,
            then links and groups, and a group holds links only. */}
        {sidebar.length === 0 && bootstrapLoading && (
          <Stack spacing={1} sx={{ px: 1.25, pt: 0.5 }} aria-label="Loading navigation">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={26} />)}
          </Stack>
        )}
        {sidebar.map((section, index) => (
          <React.Fragment key={section.id}>
            {renderSectionLabel(section.label, index === 0)}
            {section.items.map((item) => (item.kind === 'group' ? renderGroup(item) : item.hidden ? null : renderLink(item)))}
          </React.Fragment>
        ))}
      </List>
      )}
      {!isMobile && sidebarPosition === 'left' && (
        <Box
          sx={(theme) => ({
            px: effectiveCollapsed ? 1 : 1.5,
            pt: 1.5,
            pb: 1.5,
            borderTop: `1px solid ${theme.tokens.border.subtle}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          })}
        >
          {/* Sign out. It sits in the footer rather than in the nav list
              because it is not a place you go, and behind its own rule so it
              reads as separate from the destinations above. Also present in
              the avatar menu, which is the conventional home for it. */}
          <Tooltip title={effectiveCollapsed ? 'Logout' : ''} placement="right">
            <ListItemButton
              key="logout"
              disableRipple
              disableTouchRipple
              onClick={() => { handleLogout(); setMobileOpen(false); }}
              sx={(theme) => ({
                ...navItemSx(effectiveCollapsed, false)(theme),
                mb: 1,
                color: theme.tokens.text.secondary,
                // Red on hover, not at rest: signing out is destructive enough
                // to warn about when you reach for it, not alarming enough to
                // shout from the sidebar the whole time.
                '&:hover': {
                  bgcolor: theme.tokens.semantic.dangerSoft,
                  color: theme.tokens.semantic.danger,
                  '& .menu-ic': { color: theme.tokens.semantic.danger },
                },
              })}
            >
              {effectiveCollapsed ? (
                <LogoutRounded className="menu-ic" sx={{ fontSize: 18 }} />
              ) : (
                <>
                  <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                    <LogoutRounded className="menu-ic" sx={{ fontSize: 18 }} />
                  </Box>
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '0.8125rem',
                      letterSpacing: '0.005em',
                    }}
                  />
                </>
              )}
            </ListItemButton>
          </Tooltip>

          <Box sx={(theme) => ({
            borderTop: `1px solid ${theme.tokens.border.subtle}`,
            mb: 0.5,
          })} />

          {/* "Talk to our team" support card — prominent, brand-tinted CTA
              so users always know how to reach us. Collapsed sidebar shows
              an icon-only button with the same affordance. */}
          {effectiveCollapsed ? (
            <Tooltip title="Talk to our team" placement="right">
              <IconButton
                onClick={() => setSupportOpen(true)}
                aria-label="Talk to our team"
                sx={(theme) => ({
                  alignSelf: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: `${theme.ds.radius.md}px`,
                  bgcolor: theme.tokens.brand.primarySoft,
                  color: theme.tokens.brand.primary,
                  transition: `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                  '&:hover': {
                    bgcolor: theme.tokens.brand.primary,
                    color: theme.tokens.brand.onPrimary,
                  },
                })}
              >
                <ChatBubbleOutlineRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <ButtonBase
              onClick={() => setSupportOpen(true)}
              aria-label="Talk to our team"
              sx={(theme) => ({
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.25,
                py: 1.125,
                borderRadius: `${theme.ds.radius.md}px`,
                textAlign: 'left',
                bgcolor: theme.tokens.brand.primarySoft,
                border: `1px solid ${alpha(theme.tokens.brand.primary, 0.18)}`,
                transition: `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}, border-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}, transform ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                '&:hover': {
                  bgcolor: alpha(theme.tokens.brand.primary, 0.14),
                  borderColor: alpha(theme.tokens.brand.primary, 0.32),
                  '& .help-cta-arrow': { transform: 'translateX(2px)' },
                },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow: theme.tokens.shadow.focusRing,
                },
              })}
            >
              <Box sx={(theme) => ({
                width: 32,
                height: 32,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: `${theme.ds.radius.sm}px`,
                bgcolor: theme.tokens.surface.raised,
                color: theme.tokens.brand.primary,
                border: `1px solid ${alpha(theme.tokens.brand.primary, 0.2)}`,
              })}>
                <ChatBubbleOutlineRounded sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={(theme) => ({
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: theme.tokens.text.primary,
                  })}
                >
                  Talk to our team
                </Typography>
                <Typography
                  noWrap
                  sx={(theme) => ({
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    lineHeight: 1.3,
                    mt: 0.125,
                    color: theme.tokens.text.secondary,
                  })}
                >
                  Get help when you need it
                </Typography>
              </Box>
              <KeyboardArrowRight
                className="help-cta-arrow"
                sx={(theme) => ({
                  fontSize: 16,
                  color: theme.tokens.brand.primary,
                  flexShrink: 0,
                  transition: `transform ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                })}
              />
            </ButtonBase>
          )}

        </Box>
      )}
    </Box>
  );

  // Generate breadcrumbs based on current path
  const getBreadcrumbs = () => {
    const crumbs: Array<{ label: string; path?: string }> = [
      { label: userInfo.tenant?.name || 'Workspace', path: homePath(navigation, brandingContext.branding?.defaultHomePage) }
    ];

    // Where the sidebar puts this page, when it puts it inside a group or
    // names it as a board: "Sales › Pulse", "New Business › Placement Cohort
    // Board". The crumbs then say what the reader just clicked through.
    // Everything else keeps the crumbs below.
    if (trail && (trail.group || trail.link.internal_path || trail.link.path.startsWith('/dashboard/'))) {
      crumbs.push({ label: trail.group ? trail.group.label : trail.section.label });
      crumbs.push({ label: trail.link.label });
      return crumbs;
    }
    
    if (location.pathname === '/' || location.pathname.startsWith('/copilot')) {
      crumbs.push({ label: 'Copilot', path: '/copilot' });
    } else if (location.pathname.startsWith('/dashboard/')) {
      crumbs.push({ label: 'Dashboard', path: '/dashboard' });
      if (currentDashboardId) {
        crumbs.push({ label: dashboardTitle });
      }
    } else if (location.pathname.startsWith('/spaces/')) {
      crumbs.push({ label: 'Spaces', path: '/spaces' });
      if (currentSpaceId) {
        crumbs.push({ label: spaceName });
      }
    } else if (location.pathname.includes('/connectors/')) {
      crumbs.push({ label: 'Connectors', path: '/connectors' });
      crumbs.push({ label: 'Details' });
    } else if (location.pathname === '/data-sync') {
      crumbs.push({ label: 'Data Model', path: '/data-model' });
      crumbs.push({ label: 'Build Data Model' });
    } else if (location.pathname.startsWith('/data-sync/')) {
      crumbs.push({ label: 'Data Model', path: '/data-model' });
      crumbs.push({ label: 'Build Data Model', path: '/data-sync' });
      crumbs.push({ label: 'Run' });
    } else if (location.pathname.startsWith('/users')) {
      // People lives in the SETTINGS sidebar group but routes flat at /users.
      // Single-segment crumb keeps the bar short and matches every other
      // top-level page (Connectors, Data Model, Knowledge Base, etc.).
      crumbs.push({ label: 'People' });
    } else if (isAgentActive) {
      // Agents › ROCCO › Queue — the agent by name, because inside one the
      // sidebar no longer says which it is.
      crumbs.push({ label: 'Agents', path: '/agents' });
      const [, , address, section] = location.pathname.split('/');
      // The address is the kind, or kind and id for a further agent of a kind.
      const agentKind = kindOfAddress(address);
      crumbs.push({ label: AGENT_NAMES[agentKind] ?? agentKind, path: `/agents/${address}/spotlight` });
      if (section) {
        const item = (AGENT_SECTIONS[agentKind] ?? []).flatMap((group) => group.items).find((i) => i.id === section);
        crumbs.push({ label: item?.label ?? section.charAt(0).toUpperCase() + section.slice(1).replace(/-/g, ' ') });
      }
    } else if (location.pathname.startsWith('/agents')) {
      const isList = location.pathname === '/agents';
      crumbs.push(
        isList
          ? { label: 'Agents' }
          : { label: 'Agents', path: '/agents' },
      );
      if (!isList) crumbs.push({ label: 'Details' });
    } else if (location.pathname.startsWith('/lead-to-cash')) {
      crumbs.push({ label: 'Live Monitoring' });
      crumbs.push({ label: 'Lead-to-Cash' });
    } else if (location.pathname.startsWith('/monitoring/dialing-opportunity')) {
      crumbs.push({ label: 'Live Monitoring' });
      crumbs.push({ label: 'Dialing Opportunity' });
    } else if (location.pathname.startsWith('/monitoring/contact-optimization')) {
      crumbs.push({ label: 'Live Monitoring' });
      crumbs.push({ label: 'Contact Pulse' });
    } else if (location.pathname.startsWith('/broker-monitor')) {
      crumbs.push({ label: 'Live Monitoring' });
      crumbs.push({ label: 'Broker Activity Monitor' });
    } else if (location.pathname.startsWith('/predictive-models')) {
      // Predictive Models hub + family pages. Two-segment crumb when
      // we're inside a family ("Predictive Models > Propensity"); single
      // when we're on the hub itself.
      crumbs.push({ label: 'Predictive Models', path: '/predictive-models' });
      if (location.pathname.startsWith('/predictive-models/propensity')) {
        // Drop the path on the leaf so it isn't a clickable link to the
        // page we're already on.
        const isList = location.pathname === '/predictive-models/propensity';
        crumbs.push(
          isList
            ? { label: 'Propensity' }
            : { label: 'Propensity', path: '/predictive-models/propensity' },
        );
        if (!isList) crumbs.push({ label: 'Configuration' });
      } else if (location.pathname.startsWith('/predictive-models/contact-optimization')) {
        crumbs.push({ label: 'Contact Optimization' });
      }
    } else if (location.pathname.startsWith('/settings') || location.pathname.startsWith('/branding') || location.pathname.startsWith('/profile')) {
      // Settings sub-pages get a "Settings > <subpage>" two-segment crumb so
      // the tenant > Settings > Branding context reads naturally. The parent
      // "Settings" crumb is intentionally non-clickable (no path) — there's
      // no /settings index page; the AI Configuration tab is the de-facto
      // landing target but linking to it from a generic "Settings" word
      // would be misleading.
      const settingsItem = settingsMenuItems.find(item => location.pathname.startsWith(item.path))
        // /branding and /profile are legacy aliases that redirect to /settings/*
        // — match them to the canonical settings entry so the crumb still resolves.
        || (location.pathname.startsWith('/branding') ? settingsMenuItems.find(i => i.key === 'settings-branding') : null)
        || (location.pathname.startsWith('/profile') ? settingsMenuItems.find(i => i.key === 'settings-account') : null);
      crumbs.push({ label: 'Settings' });
      if (settingsItem) {
        crumbs.push({ label: settingsItem.label });
      }
    } else {
      const currentItem = mainMenu.find(item => item.path === location.pathname);
      if (currentItem) {
        crumbs.push({ label: currentItem.label });
      }
    }
    
    return crumbs;
  };

  // Get page title and description based on current path
  const getPageInfo = () => {
    const liveItem = liveMonitoringMenuItems.find((item) => location.pathname.startsWith(item.path));
    if (liveItem) {
      switch (liveItem.key) {
        case 'lead-to-cash':
          return { title: 'Lead-to-Cash Monitor', description: 'Distribution chain health from lead through commission' };
        case 'dialing-opportunity':
          return { title: 'Dialing Opportunity', description: 'First-contact timing gaps, missed contacts, and demand-shift signal' };
        case 'sales-ops-today':
          return { title: 'Today', description: 'What the operation is doing right now, and what needs a person' };
        case 'sales-ops-plan':
          return { title: 'Plan', description: "Capacity against this month's target, and where the hours are going to waste" };
        case 'sales-ops-queue':
          return { title: 'Queue', description: 'Callable inventory ranked by expected margin, and who should take each lead' };
        case 'sales-ops-agents':
          return { title: 'Agents', description: 'Margin by producer, and every input that produced it' };
        case 'contact-optimization':
          return { title: 'Contact Pulse', description: 'Is contact rate working this week, and where the opportunity is' };
        case 'broker-monitor':
          return { title: 'Broker Activity Monitor', description: 'Charter marketplace chain health from requests through revenue, broker by broker' };
        default:
          return { title: liveItem.label, description: '' };
      }
    }
    const currentItem = mainMenu.find(item => item.path === location.pathname);
    if (currentItem) {
      switch (currentItem.key) {
        case 'connectors':
          return { title: 'Configure Connectors', description: 'Manage your data source connections' };
        case 'data-model':
          return { title: 'Data Model', description: 'View and explore your unified data model' };
        case 'bcl':
          return { title: 'Business Context', description: 'Add business meaning to your data' };
        case 'knowledge':
          return { title: 'Knowledge Base', description: 'Metrics, rules, and terms learned from conversations' };
        case 'dashboard':
          return { title: 'Dashboard', description: 'Explore insights and KPIs from your dashboards' };
        default:
          return { title: currentItem.label, description: '' };
      }
    }
    // Handle connector detail pages
    if (location.pathname.includes('/connectors/')) {
      return { title: 'Connector Details', description: 'Configure and manage your connector' };
    }
    // Handle data sync job detail pages
    if (location.pathname.startsWith('/data-sync/')) {
      return { title: 'Sync Run Details', description: 'View the progress and results of this sync run' };
    }
    // A workspace agent draws its own header.
    if (isAgentActive) {
      return { title: 'Agent', description: 'What it is doing, and what it has done' };
    }
    // Handle agent detail pages
    if (location.pathname.startsWith('/agents/')) {
      return { title: 'Agent', description: 'View configuration, run history, and trigger new runs' };
    }
    // Handle entity detail pages
    if (location.pathname.startsWith('/data-model/entity/')) {
      const name = decodeURIComponent(location.pathname.replace('/data-model/entity/', ''));
      return { title: name, description: 'Entity details - fields, relationships and business rules' };
    }
    if (currentDashboardId) {
      return { title: dashboardTitle, description: dashboardDescription };
    }
    return { title: 'Copilot', description: `Ask anything about your data - ${brandingContext.branding?.name || config.brandName} streams real answers using your connectors.` };
  };

  /** Single-line title for mobile header (avoids tenant + long breadcrumbs). */
  const mobileHeaderTitle = React.useMemo(() => {
    const p = location.pathname;
    if (p === '/' || p.startsWith('/copilot')) return 'Copilot';
    if (p.startsWith('/dashboard')) return p === '/dashboard' ? 'Dashboards' : dashboardTitle;
    if (p.startsWith('/lead-to-cash')) return 'Lead-to-Cash';
    if (p.startsWith('/monitoring/dialing-opportunity')) return 'Dialing Opportunity';
    if (p.startsWith('/sales-operations/plan')) return 'Plan';
    if (p.startsWith('/sales-operations/queue')) return 'Queue';
    if (p.startsWith('/sales-operations/agents')) return 'Agents';
    if (p.startsWith('/sales-operations')) return 'Today';
    if (p.startsWith('/monitoring/contact-optimization')) return 'Contact Pulse';
    if (p.startsWith('/broker-monitor')) return 'Broker Activity Monitor';
    if (p.startsWith('/spaces/')) return spaceName;
    if (p === '/spaces' || p === '/saved-queries') {
      const item = mainMenu.find((i) => i.path === '/spaces');
      return item?.label ?? 'Spaces';
    }
    if (p === '/connectors') return 'Connectors';
    if (p.startsWith('/connectors/')) return 'Connector';
    if (p === '/data-sync') return 'Data Sync';
    if (p.startsWith('/data-sync/')) return 'Sync run';
    if (p.startsWith('/data-model/entity/')) {
      try {
        return decodeURIComponent(p.replace('/data-model/entity/', ''));
      } catch {
        return 'Entity';
      }
    }
    if (p.startsWith('/data-model')) return 'Data Model';
    if (p.startsWith('/knowledge')) return 'Knowledge Base';
    if (p === '/sales-desk') return 'Sales desk';
    if (p === '/agents') return 'Agents';
    if (p.startsWith('/agents/')) return 'Agent';
    if (p === '/predictive-models') return 'Predictive Models';
    if (p === '/predictive-models/propensity') return 'Propensity';
    if (p.startsWith('/predictive-models/propensity/')) return 'Configure model';
    if (p.startsWith('/predictive-models/contact-optimization')) return 'Contact Optimization';
    if (p.startsWith('/predictive-models')) return 'Predictive Models';
    if (p.startsWith('/settings/ai')) return 'AI Configuration';
    if (p.startsWith('/settings/workspace')) return 'Workspace';
    if (p.startsWith('/settings/branding') || p.startsWith('/branding')) return 'Branding';
    if (p.startsWith('/settings/account') || p.startsWith('/profile')) return 'Account';
    if (p.startsWith('/settings')) return 'AI Configuration';
    if (p.startsWith('/users')) return 'People';
    if (p.startsWith('/bcl')) return 'Business Context';
    const currentItem = mainMenu.find((item) => item.path === p);
    if (currentItem) return currentItem.label;
    return null;
  }, [location.pathname, dashboardTitle, spaceName]);

  const pageInfo = getPageInfo();
  const breadcrumbs = getBreadcrumbs();

  const mobileTitleSx = {
    fontWeight: 700,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    pr: { xs: 0.5, sm: 0 },
  };
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userInfo.user?.email) return 'U';
    const email = String(userInfo.user.email);
    const name = email.split('@')[0];
    return name.charAt(0).toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', ...viewportHeightSx }}>
      {/* Side navigation — single, modern drawer surface.
          Canvas-color background lets content cards "lift" out of it
          (Linear/Notion pattern), with one hairline border. No gradient,
          no blur, no fake shadow, no rounded corners. */}
      {(() => {
        const drawerPaperSx = (theme: Theme) => ({
          width: isMobile ? drawerWidth : (effectiveCollapsed ? miniWidth : drawerWidth),
          boxSizing: 'border-box' as const,
          bgcolor: theme.tokens.sidebar.bg,
          color: theme.tokens.text.primary,
          borderRight: sidebarPosition === 'left' ? `1px solid ${theme.tokens.border.subtle}` : 'none',
          borderLeft: sidebarPosition === 'right' ? `1px solid ${theme.tokens.border.subtle}` : 'none',
          borderRadius: 0,
          backgroundImage: 'none',
          boxShadow: 'none',
          overflow: 'hidden' as const,
          transition: theme.transitions.create(['width', 'background-color'], {
            duration: theme.ds.motion.duration.normal,
          }),
          top: 0,
          ...viewportHeightSx,
          zIndex: theme.zIndex.appBar - 1,
        });
        return isMobile ? (
          <Drawer
            variant="temporary"
            anchor={sidebarPosition}
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ [`& .MuiDrawer-paper`]: drawerPaperSx }}
          >
            {drawer}
          </Drawer>
        ) : (
          <>
            <Drawer
              variant="permanent"
              anchor={sidebarPosition}
              sx={{ [`& .MuiDrawer-paper`]: drawerPaperSx }}
            >
              {drawer}
            </Drawer>
            {/* The one sidebar toggle, open or collapsed: a small round button
                on the corner where the top section's bottom line meets the
                sidebar's edge, always in view (Atlassian, Supabase). It used to
                be a button inside the header when open and, when collapsed,
                an arrow that only appeared on hovering the logo — easy to miss.
                Drawn beside the drawer, not in it: the drawer clips anything
                past its edge. It slides with the edge as the width changes. */}
            <Tooltip
              title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              placement={sidebarPosition === 'left' ? 'right' : 'left'}
            >
              <IconButton
                size="small"
                onClick={toggleCollapsed}
                aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!effectiveCollapsed}
                sx={(theme) => {
                  const edge = (effectiveCollapsed ? miniWidth : drawerWidth) - 12;
                  return {
                    position: 'fixed',
                    top: headerHeight - 12,
                    left: sidebarPosition === 'left' ? edge : undefined,
                    right: sidebarPosition === 'right' ? edge : undefined,
                    zIndex: theme.zIndex.appBar + 1,
                    width: 24,
                    height: 24,
                    bgcolor: theme.tokens.surface.raised,
                    color: theme.tokens.text.secondary,
                    border: `1px solid ${theme.tokens.border.default}`,
                    boxShadow: theme.tokens.shadow.xs,
                    transition: [
                      theme.transitions.create(['left', 'right'], { duration: theme.ds.motion.duration.normal }),
                      `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                      `color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                      `border-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                    ].join(', '),
                    '&:hover': {
                      bgcolor: theme.tokens.brand.primarySoft,
                      color: theme.tokens.brand.primary,
                      borderColor: theme.tokens.brand.primary,
                    },
                    '&:focus-visible': { outline: 'none', boxShadow: theme.tokens.shadow.focusRing },
                  };
                }}
              >
                {(sidebarPosition === 'left') === effectiveCollapsed
                  ? <KeyboardArrowRight sx={{ fontSize: 16 }} />
                  : <KeyboardArrowLeft sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </>
        );
      })()}

      {/* Main content area */}
      <Box component="main" sx={{ 
        flexGrow: 1, 
        ml: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...viewportHeightSx,
      }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header stays outside the scroll region; banners (sync, onboarding) sit directly below.
              Single hairline border, no inner shadow, raised surface. */}
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              ml: sidebarPosition === 'left' ? { xs: 0, md: `${effectiveCollapsed ? miniWidth : drawerWidth}px` } : undefined,
              mr: sidebarPosition === 'right' ? { xs: 0, md: `${effectiveCollapsed ? miniWidth : drawerWidth}px` } : undefined,
              borderBottom: `1px solid ${theme.tokens.border.subtle}`,
              height: `${headerHeight}px`,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              zIndex: theme.zIndex.appBar,
              bgcolor: theme.tokens.surface.raised,
              boxShadow: 'none',
            })}
          >
            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: { xs: 1.25, sm: 1.5, md: 2 } }}>
              {/* Left Section: mobile menu + Breadcrumbs / compact title */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1 } }}>
                {isMobile && (
                  <IconButton
                    onClick={() => setMobileOpen(true)}
                    edge="start"
                    size="small"
                    aria-label="Open navigation"
                    sx={{
                      color: 'text.primary',
                      flexShrink: 0,
                      ml: 0,
                      p: '10px',
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                )}
                {isMobile && mobileHeaderTitle != null ? (
                  <Typography component="div" sx={mobileTitleSx}>
                    {mobileHeaderTitle}
                  </Typography>
                ) : (
                <Breadcrumbs
                  separator={<NavigateNextIcon sx={(theme) => ({ fontSize: 14, color: theme.tokens.text.tertiary })} />}
                  aria-label="breadcrumb"
                  sx={{
                    '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', minWidth: 0 },
                    '& .MuiBreadcrumbs-li': { minWidth: 0, overflow: 'hidden' },
                    '& .MuiBreadcrumbs-separator': { mx: 0.75 },
                  }}
                >
                  {breadcrumbs.map((crumb, idx) => {
                    const isLast = idx === breadcrumbs.length - 1;
                    const isDashboardBreadcrumb = isLast && currentDashboardId && location.pathname.startsWith('/dashboard/');
                    if (isDashboardBreadcrumb && renamingDashboard) {
                      return (
                        <InputBase
                          key={idx}
                          value={dashboardRenameValue}
                          onChange={(e) => setDashboardRenameValue(e.target.value)}
                          autoFocus
                          onBlur={async () => {
                            if (dashboardRenamingSaving) return;
                            try {
                              setDashboardRenamingSaving(true);
                              const token = window.localStorage.getItem('klaris_jwt');
                              if (token) {
                                const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(currentDashboardId as string)}`), {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ name: dashboardRenameValue }),
                                } as RequestInit);
                                if (resp.ok) {
                                  setDashboardTitle(dashboardRenameValue);
                                  setDashboards((list) => list.map((d) => d.dashboard_id === currentDashboardId ? { ...d, name: dashboardRenameValue } : d));
                                  try { window.dispatchEvent(new CustomEvent('dashboard:renamed', { detail: { id: currentDashboardId, name: dashboardRenameValue } })); } catch {}
                                }
                              }
                            } catch {}
                            finally { setRenamingDashboard(false); setDashboardRenamingSaving(false); }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
                            if (e.key === 'Escape') { setRenamingDashboard(false); }
                          }}
                          sx={(theme) => ({
                            px: 1,
                            py: 0.25,
                            border: `1px solid ${theme.tokens.border.default}`,
                            borderRadius: `${theme.ds.radius.sm}px`,
                            minWidth: 150,
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            '&.Mui-focused': { borderColor: theme.tokens.brand.primary, boxShadow: theme.tokens.shadow.focusRing },
                          })}
                        />
                      );
                    }
                    
                    return crumb.path && !isLast ? (
                      <MuiLink
                        key={idx}
                        onClick={() => navigate(crumb.path!)}
                        sx={(theme) => ({
                          cursor: 'pointer',
                          textDecoration: 'none',
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: theme.tokens.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          transition: `color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                          '&:hover': { color: theme.tokens.text.primary },
                        })}
                      >
                        {idx === 0 && <HomeRounded sx={(theme) => ({ fontSize: 14, color: theme.tokens.text.tertiary })} />}
                        {crumb.label}
                      </MuiLink>
                    ) : (
                      <Typography
                        key={idx}
                        sx={(theme) => ({
                          fontSize: '0.8125rem',
                          fontWeight: isLast ? 600 : 500,
                          color: isLast ? theme.tokens.text.primary : theme.tokens.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          maxWidth: { xs: 180, md: 360 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        })}
                      >
                        {idx === 0 && <HomeRounded sx={(theme) => ({ fontSize: 14, color: theme.tokens.text.tertiary })} />}
                        {crumb.label}
                      </Typography>
                    );
                  })}
                </Breadcrumbs>
                )}
                
                {/* Dashboard rename button (only show for dashboard detail) */}
                {currentDashboardId && location.pathname.startsWith('/dashboard/') && !renamingDashboard && (
                  <Tooltip title="Rename dashboard">
                    <IconButton 
                      size="small" 
                      onClick={() => { 
                        setDashboardRenameValue(dashboardTitle); 
                        setRenamingDashboard(true);
                      }} 
                      aria-label="Rename dashboard"
                      sx={{ 
                        opacity: 0.5, 
                        '&:hover': { opacity: 1 },
                        ml: 0.5,
                      }}
                    >
                      <EditRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Right cluster — refined enterprise toolbar:
                  contact / theme toggle / settings menu, plus an avatar pill
                  that anchors the user menu. All ghost icon-buttons, no
                  loud accent fills. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                <Tooltip title={brandingContext.mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
                  <IconButton
                    onClick={brandingContext.toggleMode}
                    size="small"
                    aria-label="Toggle theme"
                    sx={{ width: 32, height: 32, display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    {brandingContext.mode === 'light'
                      ? <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
                      : <LightModeOutlinedIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                {/* Vertical divider */}
                <Box sx={(theme) => ({ width: '1px', height: 20, bgcolor: theme.tokens.border.subtle, mx: 0.75, display: { xs: 'none', sm: 'block' } })} />
                {/* Avatar pill — primary user-menu trigger */}
                <Tooltip title={userInfo.user?.email || 'Account'}>
                  <ButtonBase
                    onClick={handleOpenUserMenu}
                    aria-label="Open account menu"
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.875,
                      height: 32,
                      pl: 0.5,
                      pr: { xs: 0.5, sm: 0.875 },
                      borderRadius: `${theme.ds.radius.full}px`,
                      transition: `background-color ${theme.ds.motion.duration.fast}ms ${theme.ds.motion.easing.standard}`,
                      '&:hover': { bgcolor: theme.tokens.sidebar.itemHoverBg },
                    })}
                  >
                    <Avatar sx={(theme) => ({
                      width: 26,
                      height: 26,
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      bgcolor: theme.tokens.brand.primarySoft,
                      color: theme.tokens.brand.primary,
                    })}>
                      {getUserInitials()}
                    </Avatar>
                    <ExpandMore sx={(theme) => ({ fontSize: 16, color: theme.tokens.text.tertiary, display: { xs: 'none', sm: 'block' } })} />
                  </ButtonBase>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          {/* Data model / sync job progress — same column slot as mobile Getting started (below header, above scroll) */}
          <Box sx={{ flexShrink: 0 }}>
            {/* Persistent guest strip (anonymous users) shares this band so it
                shows on every page until the workspace is claimed. */}
            <GuestBanner
              ml={sidebarPosition === 'left' ? { xs: 0, md: effectiveCollapsed ? miniWidth : drawerWidth } : undefined}
              mr={sidebarPosition === 'right' ? { xs: 0, md: effectiveCollapsed ? miniWidth : drawerWidth } : undefined}
            />
            <SyncBanner
              ml={sidebarPosition === 'left' ? { xs: 0, md: effectiveCollapsed ? miniWidth : drawerWidth } : undefined}
              mr={sidebarPosition === 'right' ? { xs: 0, md: effectiveCollapsed ? miniWidth : drawerWidth } : undefined}
            />
            <PageBanner
              ml={sidebarPosition === 'left' ? { xs: 0, md: effectiveCollapsed ? miniWidth : drawerWidth } : undefined}
              mr={sidebarPosition === 'right' ? { xs: 0, md: effectiveCollapsed ? miniWidth : drawerWidth } : undefined}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: isFullHeightPage ? 'hidden' : 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={isCopilotActive ? {
              pl: sidebarPosition === 'left' ? { xs: 0, md: `${effectiveCollapsed ? miniWidth : drawerWidth}px` } : undefined,
              pr: sidebarPosition === 'right' ? { xs: 0, md: `${effectiveCollapsed ? miniWidth : drawerWidth}px` } : undefined,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            } : {
              // Equal horizontal inset on mobile (was p:3 + pl:0 on xs → large right-only gutter).
              // Note: `pb` is intentionally NOT set here. There's a long-standing
              // Chrome quirk where padding-bottom on a `flex: 1` child of an
              // `overflow: auto` flex container is clipped from the scroll
              // region once content overflows — see the explicit spacer Box
              // immediately after <Outlet/> below for the bottom breathing room.
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: l2cChatSplit ? 'hidden' : undefined,
              pt: isWideBoard ? 1.5 : 3,
              px: isWideBoard ? { xs: 1, sm: 1, md: 1 } : { xs: 2, sm: 2.5, md: 3 },
              pl:
                sidebarPosition === 'left'
                  ? { md: `calc(${effectiveCollapsed ? miniWidth : drawerWidth}px + 16px)` }
                  : undefined,
              pr:
                sidebarPosition === 'right'
                  ? { md: `calc(${effectiveCollapsed ? miniWidth : drawerWidth}px + 16px)` }
                  : undefined,
            }}
          >
            <Outlet context={{ fetchDashboards }} />
            {/* Bottom breathing room for every routed page. Implemented as an
                explicit spacer (rather than `pb` on the wrapper) because:
                  1) Dense pages — Data Model, Knowledge Base, Dashboard list —
                     ended with cards/tables crushed against the viewport edge.
                  2) `padding-bottom` on a `flex: 1` child of an `overflow: auto`
                     flex parent is unreliably clipped in Chrome (crbug 822958),
                     so the previous `pb: 3` on the wrapper was effectively
                     swallowed once content overflowed. A plain block-level
                     spacer always contributes to the scroll height.
                Skipped for the Copilot full-height chat layout, which has its
                own bottom anchor (the chat input). */}
            {!isFullHeightPage && (
              <Box
                aria-hidden
                sx={{ flexShrink: 0, height: { xs: 48, sm: 64, md: 80 } }}
              />
            )}
          </Box>
          </Box>
        </Box>
        
      </Box>

      <SupportRequestDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        tenantId={tenantId}
        onSuccess={() => notify('Thanks - we received your message and will get back to you soon.', 'success')}
      />

      {(() => {
        // Lifted out of the IIFE inside <children> so the native AppDialog
        // primary/secondary action slots can also reference it — keeps the
        // footer buttons consistent with the rest of the app instead of the
        // bespoke action bar this modal used to render.
        const pathHint = getOnboardingPath(tenantId);
        const datasetName = getOnboardingDataset(tenantId);
        const isSampleData = pathHint === 'sample' || !!datasetName;
        const firstDashboardId = dashboards[0]?.dashboard_id || null;

        const handlePrimary = () => {
          if (isSampleData) {
            trackOnboardingCompletionCtaClicked({
              cta: 'connect_own_data',
              has_first_dashboard: Boolean(firstDashboardId),
            });
            closeOnboardingDoneModal();
            navigate('/connectors');
            return;
          }
          trackOnboardingCompletionCtaClicked({
            cta: 'view_dashboard',
            has_first_dashboard: Boolean(firstDashboardId),
          });
          closeOnboardingDoneModal();
          navigate(firstDashboardId ? `/dashboard/${firstDashboardId}` : '/dashboard');
        };

        const handleSecondary = () => {
          trackOnboardingCompletionModalDismissed({ action: 'explore' });
          closeOnboardingDoneModal();
        };

        return (
      <AppDialog
        open={onboardingDoneModalOpen}
        onClose={handleOnboardingCompletionDialogClose}
        size="sm"
        hideCloseButton
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.tokens.brand.primarySoft,
              color: theme.tokens.brand.primary,
              flexShrink: 0,
            })}>
              <CelebrationRounded sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={(theme) => ({
                ...theme.typography.eyebrow,
                color: theme.tokens.brand.primary,
                mb: 0.25,
              })}>
                Setup complete
              </Typography>
              <Typography variant="h5" sx={(theme) => ({
                fontWeight: 700,
                letterSpacing: '-0.018em',
                color: theme.tokens.text.primary,
                lineHeight: 1.2,
              })}>
                You're up and running
              </Typography>
            </Box>
          </Box>
        }
        contentSx={{ px: { xs: 2, sm: 3.5 }, pt: 1, pb: 2.5 }}
        secondaryAction={{
          label: 'Explore on my own',
          onClick: handleSecondary,
          color: 'inherit',
        }}
        primaryAction={{
          label: isSampleData ? 'Connect my own data' : 'View my dashboard',
          onClick: handlePrimary,
        }}
      >
        {(() => {
          // Read personalised context stored during onboarding (may be missing for users who started before these keys existed)
          // Wall-clock minutes since per-tenant TTV start (OTP verify + sample dataset pick), not "active working time".
          // If the user was away for hours/days, minute counts are misleading - fall back to neutral copy.
          const startTs = getOnboardingTtvStartMs(tenantId);
          const elapsedMs = startTs ? Date.now() - startTs : 0;
          const elapsedMin = Math.max(1, Math.round(elapsedMs / 60000));
          const timeLabel = elapsedMin === 1 ? '1 minute' : `${elapsedMin} minutes`;
          const MEANINGFUL_CLOCK_MS = 90 * 60 * 1000; // beyond ~90m since OTP, don't claim "insight in X minutes"
          const clockStillMeaningful = startTs > 0 && elapsedMs <= MEANINGFUL_CLOCK_MS;
          const datasetName = getOnboardingDataset(tenantId);
          const pathHint = getOnboardingPath(tenantId);
          const isOwnDataPath = pathHint === 'own_data';
          const firstQ = getOnboardingFirstQ(tenantId);
          const firstDashboard = dashboards[0]?.name || null;
          const isSampleData = pathHint === 'sample' || !!datasetName;

          const connectorDone = onboardingSteps.some((s) => s.id === 'connector_added' && s.completed);
          const questionDone = onboardingSteps.some((s) => s.id === 'first_question_asked' && s.completed);
          const widgetDone = onboardingSteps.some((s) => s.id === 'widget_added' && s.completed);

          const connectedValue = datasetName
            ? datasetName
            : pathHint === 'own_data'
              ? 'Your data warehouse (via connectors)'
              : connectorDone
                ? 'Your live database connector'
                : 'Live workspace - connect data anytime';

          const askedDisplay = firstQ
            ? `"${firstQ.length > 60 ? firstQ.slice(0, 57) + '…' : firstQ}"`
            : questionDone
              ? 'A question in plain English - answered with charts'
              : 'Natural-language question → instant insight';

          const builtValue = firstDashboard
            ? firstDashboard
            : widgetDone
              ? 'A dashboard with your pinned widget'
              : 'Your home for charts and reports';

          const achievements = [
            { Icon: StorageRounded, label: 'Connected', value: connectedValue },
            { Icon: QuestionAnswerRounded, label: 'Asked', value: askedDisplay },
            { Icon: DashboardRounded, label: 'Built', value: builtValue },
          ];

          const insightHeadline = (() => {
            if (isOwnDataPath) {
              return 'Your live data workspace is ready.';
            }
            if (isSampleData && clockStillMeaningful) {
              return `Your first data insight in ${timeLabel}.`;
            }
            return 'Your first data insight is ready.';
          })();

          const insightSubline = isOwnDataPath
            ? 'Connectors and your model power answers in plain English - the same work often takes a BI team days.'
            : "A BI analyst would've taken 2-3 days to produce the same.";

          return (
            <>
                <Box>
                  <Box
                    sx={(theme) => ({
                      borderRadius: `${theme.ds.radius.lg}px`,
                      p: 2.25,
                      mb: 2.5,
                      border: `1px solid ${theme.tokens.border.subtle}`,
                      bgcolor: theme.tokens.surface.sunken,
                    })}
                  >
                    <Stack direction="row" spacing={1.75} alignItems="flex-start">
                      <Box
                        sx={(theme) => ({
                          width: 36,
                          height: 36,
                          borderRadius: `${theme.ds.radius.sm}px`,
                          flexShrink: 0,
                          bgcolor: theme.tokens.brand.primarySoft,
                          color: theme.tokens.brand.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        })}
                      >
                        <AccessTimeRounded sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, pt: 0.125 }}>
                        <Typography
                          sx={(theme) => ({
                            fontWeight: 700,
                            lineHeight: 1.35,
                            letterSpacing: '-0.012em',
                            mb: 0.5,
                            fontSize: '0.9375rem',
                            color: theme.tokens.text.primary,
                          })}
                        >
                          {insightHeadline}
                        </Typography>
                        <Typography sx={(theme) => ({
                          fontSize: '0.8125rem',
                          lineHeight: 1.55,
                          color: theme.tokens.text.secondary,
                        })}>
                          {insightSubline}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                {/* Achievement list */}
                <Box sx={(theme) => ({
                  borderRadius: `${theme.ds.radius.lg}px`,
                  border: `1px solid ${theme.tokens.border.subtle}`,
                  overflow: 'hidden',
                  mb: 2,
                  bgcolor: theme.tokens.surface.raised,
                })}>
                  {achievements.map(({ Icon, label, value }, idx) => (
                    <Box key={idx} sx={(theme) => ({
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 2, py: 1.5,
                      borderBottom: idx < achievements.length - 1
                        ? `1px solid ${theme.tokens.border.subtle}`
                        : 'none',
                    })}>
                      {/* Success check */}
                      <Box sx={(theme) => ({
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        bgcolor: theme.tokens.semantic.successSoft,
                        color: theme.tokens.semantic.success,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      })}>
                        <TaskAltRounded sx={{ fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={(theme) => ({
                          ...theme.typography.eyebrow,
                          color: theme.tokens.text.tertiary,
                          mb: 0.125,
                        })}>
                          {label}
                        </Typography>
                        <Typography noWrap title={String(value)} sx={(theme) => ({
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          color: theme.tokens.text.primary,
                          minWidth: 0,
                        })}>
                          {value}
                        </Typography>
                      </Box>
                      <Icon sx={(theme) => ({ fontSize: 16, color: theme.tokens.text.tertiary, flexShrink: 0 })} />
                    </Box>
                  ))}
                </Box>

                <Typography sx={(theme) => ({
                  fontSize: '0.8125rem',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  px: 1,
                  color: theme.tokens.text.tertiary,
                })}>
                  {isSampleData
                    ? "You're on sample data — explore freely, then connect your own sources when you're ready."
                    : 'Your workspace is ready. Keep exploring or refine connectors for deeper coverage.'}
                </Typography>
                </Box>
            </>
          );
        })()}
      </AppDialog>
        );
      })()}

      {/* User Profile Menu — refined account dropdown.
          Header shows full identity (name + email + tenant) so the menu
          earns its space; items are tokenised. */}
      <Menu
        anchorEl={userMenuEl}
        open={openUserMenu}
        onClose={handleCloseUserMenu}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: (theme) => ({
            mt: 1,
            minWidth: 280,
            borderRadius: `${theme.ds.radius.lg}px`,
            border: `1px solid ${theme.tokens.border.default}`,
            boxShadow: theme.tokens.shadow.lg,
            backgroundImage: 'none',
            overflow: 'hidden',
            p: 0,
          }),
        }}
      >
        {/* Account header */}
        <Box sx={(theme) => ({
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderBottom: `1px solid ${theme.tokens.border.subtle}`,
          bgcolor: theme.tokens.surface.sunken,
        })}>
          <Avatar sx={(theme) => ({
            width: 36,
            height: 36,
            fontSize: '0.8125rem',
            fontWeight: 700,
            bgcolor: theme.tokens.brand.primary,
            color: theme.tokens.brand.onPrimary,
          })}>
            {getUserInitials()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={(theme) => ({ fontSize: '0.875rem', fontWeight: 600, color: theme.tokens.text.primary, lineHeight: 1.3 })}>
              {(userInfo.user as any)?.full_name || userInfo.user?.email || 'Account'}
            </Typography>
            <Typography noWrap sx={(theme) => ({ fontSize: '0.75rem', color: theme.tokens.text.tertiary, lineHeight: 1.3 })}>
              {userInfo.user?.email || ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => { handleCloseUserMenu(); navigate('/settings/account'); }}
            sx={(theme) => ({ mx: 0.5, px: 1.25, py: 0.875, gap: 1.25, borderRadius: `${theme.ds.radius.sm}px` })}
          >
            <PersonRounded sx={(theme) => ({ fontSize: 18, color: theme.tokens.text.tertiary })} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Account</Typography>
          </MenuItem>
          {/* Settings lands on /settings/ai, which is guarded — without this a
              member clicking it is bounced to Copilot with no explanation. */}
          {can('manage_workspace') && (
          <MenuItem
            onClick={() => { handleCloseUserMenu(); navigate('/settings/ai'); }}
            sx={(theme) => ({ mx: 0.5, px: 1.25, py: 0.875, gap: 1.25, borderRadius: `${theme.ds.radius.sm}px` })}
          >
            <SettingsRounded sx={(theme) => ({ fontSize: 18, color: theme.tokens.text.tertiary })} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Settings</Typography>
          </MenuItem>
          )}
          {/* "Help & support" lived here too, opening the same dialog as the
              "Talk to our team" card at the foot of the sidebar. One action,
              one place: the card is the deliberate, prominent one. */}
        </Box>
        <Divider sx={(theme) => ({ borderColor: theme.tokens.border.subtle })} />
        <Box sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => { handleCloseUserMenu(); handleLogout(); }}
            sx={(theme) => ({
              mx: 0.5,
              px: 1.25,
              py: 0.875,
              gap: 1.25,
              borderRadius: `${theme.ds.radius.sm}px`,
              color: theme.tokens.semantic.danger,
              '&:hover': { bgcolor: theme.tokens.semantic.dangerSoft },
            })}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Sign out</Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Box>
  );
}
